// =============================================================================
// LP Express — Advertorial template content model.
//
// An "Advertorial" landing page is a long-form editorial-style article (ported
// from the longform-editorial advertorial design): a labeled ad-disclosure bar
// → breadcrumb → headline + dek → author byline (name / date / verified badge /
// AI avatar) → a sequence of article body sections (heading, lead, paragraphs,
// optional inline editorial image, optional numbered steps or bullets, optional
// inline CTA) → a testimonial block (REAL reviews only) → an offer/pricing
// block with an optional callout → a fake-but-clearly-authored comment section
// → legal disclaimer → a sticky product recommendation rail (desktop) → a
// sticky bottom CTA bar (mobile).
//
// The whole advertorial is stored as ONE structured blob at
// `generatedContent.advertorial` (alongside `templateType: "advertorial"`),
// exactly like the Quiz Funnel's `quiz` and the Listicle's `listicle`. It is
// generated in a single shot by a dedicated Sim Studio workflow
// (SIM_LP_ADVERTORIAL_URL) and edited in place through the SAME
// generatedContent path-based `setText` ops the other templates use — e.g.
// ["advertorial","sections",0,"heading"].
//
// AI imagery lives SEPARATELY under `generatedContent.advertorialImages` — an
// array of per-slot records ({ key, imageUrl, imageStatus, altText }) driven by
// the standard LP Express single-slot image pipeline (per-slot pending/ready/
// failed status, per-slot retry, startup resume). The sticky rail's product
// image is NEVER AI-generated: it always comes from the brand's real product
// photos (media picker only). Testimonials always come from the brand's REAL
// review data (via the review picker) — the workflow's copy never fabricates
// customer reviews. Prices, ratings, review counts, discounts and savings
// figures are likewise DERIVED from the real product/offer/sale/review data at
// render time — the workflow's copy never invents numbers, and discount
// framing only appears when a real active discount exists on the run.
//
// This module is pure + dependency-free so it runs identically on the client
// (renderer + editor) and the server (generation/normalization).
//
// ─── Sim workflow contract (SIM_LP_ADVERTORIAL_URL) ──────────────────────────
//
// INPUT (top-level fields on the Start block — called with wrapInput=false,
// identical to the quiz/listicle workflow envelopes):
//   {
//     "brandInfo":      string  // serialized brand doctrine/profile
//     "personaInfo":    string  // serialized persona persuasion profile
//     "productInfo":    string  // serialized product/offer attributes
//     "narrativeAngle": string  // serialized narrative angle
//     "offerInfo":      string  // serialized offer/pricing/sale context
//     "feedback":       string  // optional free-form regeneration steering
//     "currentCopy":    string  // optional JSON of the CURRENT advertorial
//                               // blob (set on regeneration so the workflow
//                               // refines instead of starting from scratch)
//   }
//
// OUTPUT: a single JSON object (may be ```json-fenced under output.content —
// the extractor strips fences) matching LpAdvertorialContent below. snake_case
// aliases are accepted for every camelCase key. Guidance for the workflow:
//   • 5–8 body sections telling a first-person editorial story: hook →
//     problem/struggle → discovery → named mechanism ("why it works") → how to
//     use (steps) → results → who it's for (bullets) → wrap-up.
//   • Set `image: true` on 3–4 sections that deserve an inline editorial
//     photo (the renderer assigns AI slots section-1..section-4 in order).
//   • Include an inline `ctaText` on 2–3 sections (mid-article CTAs).
//   • `reviewsBlock.items` are 3 testimonial cards ({ name, text, rating })
//     in persona voice — clearly editable placeholders that operators are
//     expected to replace with REAL reviews via the review picker when the
//     brand has review data.
//   • `comments` items are clearly-casual reader comments in persona voice —
//     NOT product reviews (reviews render in the testimonial block).
//   • Do NOT put prices, ratings, review counts or dollar amounts in ANY copy
//     field — those are derived from real data at render time.
// =============================================================================

export interface LpAdvertorialByline {
  name: string; // author display name, e.g. "Sarah Mitchell"
  dateLine: string; // publish date line, e.g. "Updated January 3, 2026"
  verified: boolean; // show the green verified check next to the name
}

// One article body section. All parts optional except paragraphs — the
// renderer skips empty parts. `imageSlot` is ASSIGNED by the normalizer (not
// the workflow): the first four sections whose raw payload requests an image
// get slots "section-1".."section-4" in order.
export interface LpAdvertorialSection {
  heading: string;
  lead: string; // larger-type lead paragraph (optional)
  paragraphs: string[];
  imageSlot: string; // "" | "section-1".."section-4" (AI slot key)
  steps: string[]; // numbered how-to card list (optional)
  bullets: string[]; // plain bullet list (optional)
  ctaText: string; // inline pill CTA label ("" = no inline CTA)
  // Editor-set: a hidden section stays in the blob (so it can be un-hidden)
  // but is skipped by the renderer.
  hidden?: boolean;
}

// One AI-generated testimonial card. Generated in persona voice by the Sim
// workflow and fully editable in the builder. When the brand has REAL review
// data, each card can be swapped for a real review via the standard review
// picker (the picked review is snapshotted on reviewOverrides and wins over
// the blob item) — real reviews are preferred for FTC compliance, but a brand
// with zero reviews still gets a complete, editable testimonial block.
export interface LpAdvertorialTestimonial {
  name: string; // customer display name, e.g. "Sarah M."
  text: string; // the quote body (no surrounding quote marks)
  rating: number; // 1–5 stars
}

// Testimonial block: heading/footnote copy plus the generated testimonial
// items themselves.
export interface LpAdvertorialReviewsBlock {
  heading: string;
  footnote: string;
  items: LpAdvertorialTestimonial[]; // up to 3
}

export interface LpAdvertorialOffer {
  heading: string;
  paragraphs: string[];
  ctaText: string;
  calloutTitle: string; // yellow callout card title ("" = no callout)
  calloutBody: string;
}

// Sticky desktop product rail. Rating / review count / product image are
// DERIVED (real reviews + media picker) — only labels/benefits live in copy.
export interface LpAdvertorialRail {
  recLabel: string; // e.g. "Editor's Pick: The Complete Ritual"
  benefits: string[]; // up to 5 checkmark benefit lines
  ctaText: string;
}

export interface LpAdvertorialComment {
  name: string; // commenter display name (initials are derived)
  text: string;
  meta: string; // e.g. "Like · Reply · 2d"
}

export interface LpAdvertorialComments {
  heading: string; // e.g. "What readers are saying"
  items: LpAdvertorialComment[]; // up to 6
}

export interface LpAdvertorialContent {
  adLabel: string; // top disclosure bar text, e.g. "ADVERTORIAL"
  breadcrumb: string; // e.g. "Home › Wellness › Reviews"
  headline: string;
  dek: string; // sub-headline under the h1
  byline: LpAdvertorialByline;
  sections: LpAdvertorialSection[]; // up to 10
  reviewsBlock: LpAdvertorialReviewsBlock;
  offer: LpAdvertorialOffer;
  rail: LpAdvertorialRail;
  comments: LpAdvertorialComments;
  disclaimer: string;
  stickyCtaText: string; // sticky bottom bar CTA label
}

// ── AI image slots ───────────────────────────────────────────────────────────
// The advertorial's AI-generated imagery pool, stored under
// `generatedContent.advertorialImages` as
// [{ key, imageUrl, imageStatus, altText }] and generated per-slot through the
// standard LP Express single-slot image pipeline. The variantHint steers each
// slot's Sim image-prompt call. The `avatar` slot is the author byline
// portrait; section-1..4 are inline editorial photos assigned to body sections
// in order.
export const LP_ADVERTORIAL_IMAGE_SLOTS: { key: string; variantHint: string }[] = [
  {
    key: "avatar",
    variantHint:
      "Author byline portrait: a warm, credible headshot of the article's author (a relatable person matching the brand's audience), soft natural light, plain background, shoulders-up, photographic, no text.",
  },
  {
    key: "section-1",
    variantHint:
      "Editorial article photo #1: a candid first-person lifestyle scene that matches the article's opening story (the struggle/before moment or daily routine); authentic, documentary style, no text overlays.",
  },
  {
    key: "section-2",
    variantHint:
      "Editorial article photo #2: the product being discovered or used in a real home setting (unboxing, first use, on the counter); warm natural light, authentic and unstaged.",
  },
  {
    key: "section-3",
    variantHint:
      "Editorial article photo #3: a close-up detail supporting the article's 'why it works' mechanism (texture, ingredients, preparation, or the product in hand); macro editorial style.",
  },
  {
    key: "section-4",
    variantHint:
      "Editorial article photo #4: the happy 'after' moment — the author enjoying the results in daily life; bright, optimistic, documentary style, distinct from the other photos.",
  },
];

// Section image slot keys, in assignment order.
const SECTION_IMAGE_SLOT_KEYS = ["section-1", "section-2", "section-3", "section-4"];

// ── Normalization ────────────────────────────────────────────────────────────

function asObj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
}

function str(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : typeof v === "number" ? String(v) : fallback;
}

function strArray(v: unknown, cap = 20): string[] {
  return Array.isArray(v)
    ? v.filter((x) => typeof x === "string" && (x as string).trim()).slice(0, cap)
    : [];
}

// Accept either a plain string array or the template-library's wrapped
// { items: [...] } object for steps/bullets.
function itemsArray(v: unknown, cap: number): string[] {
  if (Array.isArray(v)) return strArray(v, cap);
  const o = asObj(v);
  return strArray(o.items, cap);
}

// Normalize a raw advertorial payload (e.g. from the Sim workflow, which may
// use snake_case) into the canonical LpAdvertorialContent shape. Missing fields
// become empty strings/arrays so the renderer/editor never crash — the caller
// decides whether an empty result counts as a generation failure
// (isLpAdvertorialContentUsable). IMPORTANT: any new editable field must be
// added BOTH here and to the interfaces above, or the renderer will drop it.
export function normalizeLpAdvertorialContent(raw: unknown): LpAdvertorialContent {
  const o = asObj(raw);

  const bySrc = asObj(o.byline);
  const byline: LpAdvertorialByline = {
    name: str(bySrc.name ?? bySrc.byline_name ?? (o as any).bylineName),
    dateLine: str(
      bySrc.dateLine ?? bySrc.date_line ?? bySrc.date ?? bySrc.byline_date,
    ),
    verified: Boolean(bySrc.verified ?? bySrc.byline_verified ?? true),
  };

  // Assign section image slots positionally: the first four sections whose raw
  // payload requests an image (image: true / an image object / has_image) get
  // "section-1".."section-4" in order. A previously-normalized blob (already
  // carrying imageSlot strings) round-trips unchanged.
  let nextSlot = 0;
  const sections: LpAdvertorialSection[] = (Array.isArray(o.sections) ? o.sections : [])
    .map((s) => {
      const so = asObj(s);
      const existingSlot = str(so.imageSlot ?? so.image_slot);
      const wantsImage =
        !existingSlot &&
        Boolean(
          so.image === true ||
            (so.image && typeof so.image === "object") ||
            so.hasImage ||
            so.has_image,
        );
      let imageSlot = existingSlot;
      if (SECTION_IMAGE_SLOT_KEYS.includes(existingSlot)) {
        // keep it (editor round-trip)
      } else if (wantsImage && nextSlot < SECTION_IMAGE_SLOT_KEYS.length) {
        imageSlot = SECTION_IMAGE_SLOT_KEYS[nextSlot++];
      } else {
        imageSlot = "";
      }
      const section: LpAdvertorialSection = {
        heading: str(so.heading ?? so.title),
        lead: str(so.lead),
        paragraphs: strArray(so.paragraphs, 8),
        imageSlot,
        steps: itemsArray(so.steps, 6),
        bullets: itemsArray(so.bullets, 8),
        ctaText: str(
          so.ctaText ?? so.cta_text ?? str(asObj(so.cta).text),
        ),
      };
      if (Boolean(so.hidden)) section.hidden = true;
      return section;
    })
    .filter(
      (s) =>
        s.heading || s.lead || s.paragraphs.length || s.steps.length || s.bullets.length,
    )
    .slice(0, 10);
  // Drop any unknown slot keys so the renderer only ever looks up real slots.
  for (const s of sections) {
    if (s.imageSlot && !SECTION_IMAGE_SLOT_KEYS.includes(s.imageSlot)) s.imageSlot = "";
  }

  const rbSrc = asObj(o.reviewsBlock ?? o.reviews_block);
  // Testimonial items may arrive under reviewsBlock.items, reviewsBlock.
  // testimonials, or a top-level testimonials array (workflow variants).
  const rbItemsRaw = Array.isArray(rbSrc.items)
    ? rbSrc.items
    : Array.isArray(rbSrc.testimonials)
      ? rbSrc.testimonials
      : Array.isArray(o.testimonials)
        ? o.testimonials
        : [];
  const reviewsBlock: LpAdvertorialReviewsBlock = {
    heading: str(rbSrc.heading),
    footnote: str(rbSrc.footnote),
    items: rbItemsRaw
      .map((t) => {
        const to = asObj(t);
        const ratingRaw = Number(to.rating ?? to.stars ?? 5);
        return {
          name: str(to.name ?? to.reviewer ?? to.author),
          text: str(to.text ?? to.quote ?? to.review_text ?? to.reviewText),
          rating: Number.isFinite(ratingRaw)
            ? Math.max(1, Math.min(5, Math.round(ratingRaw)))
            : 5,
        };
      })
      .filter((t) => t.text)
      .slice(0, 3),
  };

  const ofSrc = asObj(o.offer);
  const offer: LpAdvertorialOffer = {
    heading: str(ofSrc.heading),
    paragraphs: strArray(ofSrc.paragraphs, 6),
    ctaText: str(ofSrc.ctaText ?? ofSrc.cta_text) || "Check availability",
    calloutTitle: str(ofSrc.calloutTitle ?? ofSrc.callout_title),
    calloutBody: str(ofSrc.calloutBody ?? ofSrc.callout_body),
  };

  const railSrc = asObj(o.rail);
  const rail: LpAdvertorialRail = {
    recLabel: str(railSrc.recLabel ?? railSrc.rec_label),
    benefits: strArray(railSrc.benefits, 5),
    ctaText: str(railSrc.ctaText ?? railSrc.cta_text) || offer.ctaText,
  };

  const cmSrc = asObj(o.comments);
  const comments: LpAdvertorialComments = {
    heading: str(cmSrc.heading),
    items: (Array.isArray(cmSrc.items) ? cmSrc.items : [])
      .map((c) => {
        const co = asObj(c);
        return {
          name: str(co.name),
          text: str(co.text),
          meta: str(co.meta) || "Like · Reply",
        };
      })
      .filter((c) => c.name || c.text)
      .slice(0, 6),
  };

  const stSrc = asObj(o.sticky ?? o.sticky_bar ?? o.stickyBar);

  return {
    adLabel: str(o.adLabel ?? o.ad_label) || "ADVERTORIAL",
    breadcrumb: str(o.breadcrumb),
    headline: str(o.headline),
    dek: str(o.dek ?? o.subheadline),
    byline,
    sections,
    reviewsBlock,
    offer,
    rail,
    comments,
    disclaimer: str(o.disclaimer),
    stickyCtaText:
      str(o.stickyCtaText ?? o.sticky_cta_text ?? str(stSrc.text)) || offer.ctaText,
  };
}

// True when a normalized advertorial carries enough structure to render a real
// article (a headline + at least three substantive body sections). Used by the
// generator to reject an empty Sim response instead of persisting a blank page.
export function isLpAdvertorialContentUsable(
  advertorial: LpAdvertorialContent | null | undefined,
): boolean {
  if (!advertorial) return false;
  const hasHeadline = !!advertorial.headline;
  const usableSections = Array.isArray(advertorial.sections)
    ? advertorial.sections.filter((s) => s.paragraphs.length > 0 || s.lead).length
    : 0;
  return hasHeadline && usableSections >= 3;
}
